/**
 * Web automation sites list page — `GET /web-automation/sites`.
 *
 * A site is one web application the desktop runner can drive. On its own it does nothing: agents
 * from the registry are *attached* to it (SiteAgents, a.k.a. site tasks), and an attachment is the
 * executable unit that scripts and runs hang off. That is why the row's primary action opens the
 * detail page rather than the edit form — attaching is done there.
 *
 * Mounted at `ROUTES.WEB_AUTOMATION_SITES` in `routes/CortexAppRoutes.tsx`, so this migration is
 * for a page that actually renders.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference):
 * `ShellHeader` for the title and primary action, `CordioAbpPagedDataTable` for the grid, one
 * `DeleteDialog` driven by `useDeleteDialog`, and a `.web-automation-sites` BEM block.
 *
 * WHY the page still windows and searches results itself: `GET /web-automation/sites` takes exactly
 * one filter — `isActive` — and returns a bare `WebAutomationSiteDto[]`. There is no `searchTerm`
 * and no paging. The pre-migration page matched name and URL in the browser, and so does
 * {@link matchesSearch}; {@link toClientPage} then slices the result into the page the grid asked
 * for. Client-side filtering is sound here precisely because the response is the whole set — it
 * would be wrong on a server-paged endpoint, where it would filter only the current page.
 *
 * Capabilities preserved outside the grid:
 *   - The tenant + project guard. The runner's sites are project-scoped, so without both the fetch
 *     would be unscoped; the page renders an `EmptyState` and never mounts the grid, exactly as the
 *     reference page does for a missing workspace.
 *   - The stored-credentials indicator. `hasCredentials` is the difference between a site the runner
 *     can sign into and one it cannot, so it stays in the Auth cell as a lock beside the chip.
 *   - Refetch on workspace / project switch, now expressed as `dataSource` dependencies.
 *
 * --- Column plan -------------------------------------------------------------------------------
 * field             | header    | width | cell type                   | filter
 * ------------------|-----------|-------|-----------------------------|--------------------------
 * name              | Name      | 24%   | text, clickable             | toolbar search (client)
 * url               | URL       | 26%   | text, 60 + readMore         | toolbar search (client)
 * authType          | Auth      | 16%   | custom (Chip + lock icon)   | select `authType` (loaded)
 * attachedTaskCount | Agents    | 12%   | number                      | —
 * isActive          | Status    | 10%   | custom (Active Chip)        | status tabs -> `isActive`
 * createdAt         | Created   | 12%   | date, locale                | —
 *
 * Dropped from the pre-migration table: the `description` subtitle under Name (prose that belongs
 * on the detail page — name and URL are what identify a site) and the decorative globe icon.
 *
 * "Attached Tasks" is relabelled **Agents**: the DTO field is `attachedTaskCount`, but what is
 * attached is a registry agent, and the detail page calls them agents too.
 * -----------------------------------------------------------------------------------------------
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Chip,
  CordioAbpPagedDataTable,
  ShellHeader,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { EmptyState } from '@coordio/ui/layout';
import { Lock, Plus, Trash2 } from 'lucide-react';

import {
  createCortexPagedDataSource,
  type CortexPagedQuery,
} from '../components/data-display/cortexPagedDataSource';
import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { webAutomationService, type WebAutomationSiteDto } from '../services/webAutomationService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import type { PagedResult } from '../types/tenant';

import './WebAutomationSitesListPage.scss';

/**
 * Quick-filter tabs.
 *
 * `value` is load-bearing twice: the grid writes it to the toolbar's `paramKey`, AND it builds each
 * tab's test id as `cordio-datatable-status-tab-<value>`. GRID-13 asserts on `-all` / `-true` /
 * `-false`, so the All tab must carry the literal `'all'` — an empty value would emit
 * `cordio-datatable-status-tab-` and the checklist would select nothing. `'all'` is a UI concept
 * the API rejects, hence the synthetic {@link STATUS_TAB_PARAM_KEY} that {@link toIsActiveFilter}
 * resolves to the endpoint's `isActive` argument.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/** The synthetic toolbar key the status tabs write to. Never sent to the API as-is. */
const STATUS_TAB_PARAM_KEY = 'status';

/**
 * Resolves the synthetic status tab into the endpoint's one supported argument.
 *
 * All becomes `undefined` — "no filter at all" — rather than a third state, because
 * `getSites(undefined)` omits the query parameter entirely.
 */
function toIsActiveFilter(query: CortexPagedQuery): boolean | undefined {
  const status = query[STATUS_TAB_PARAM_KEY];
  if (status === 'true') return true;
  if (status === 'false') return false;
  return undefined;
}

/** Free-text match over the two fields that identify a site, as the pre-migration page did. */
function matchesSearch(site: WebAutomationSiteDto, searchTerm: string): boolean {
  const needle = searchTerm.trim().toLowerCase();
  if (needle === '') return true;
  return site.name.toLowerCase().includes(needle) || site.url.toLowerCase().includes(needle);
}

/**
 * Wraps an unpaged array response as the one page the grid asked for.
 *
 * `totalCount` is exact — the endpoint returns the whole (filtered) set, so the client knows the
 * true total even though the endpoint never states one.
 */
function toClientPage<T>(items: readonly T[], pageNumber: number, pageSize: number): PagedResult<T> {
  const start = Math.max(0, (pageNumber - 1) * pageSize);
  return {
    items: items.slice(start, start + pageSize) as T[],
    totalCount: items.length,
    pageNumber,
    pageSize,
    totalPages: Math.max(1, Math.ceil(items.length / pageSize)),
    hasPreviousPage: pageNumber > 1,
    hasNextPage: start + pageSize < items.length,
  };
}

/**
 * Loads the Auth filter's options from the sites themselves.
 *
 * `authType` is a free string on the DTO, not an enum the client can enumerate, so a hard-coded
 * list would silently omit whatever the next site declares. The grid runs this once, the first time
 * the filter popover opens, and keeps the result.
 */
async function loadAuthTypeOptions(): Promise<ReadonlyArray<{ label: string; value: string }>> {
  const sites = await webAutomationService.getSites();
  const authTypes = new Set<string>();
  for (const site of sites) {
    if (site.authType) authTypes.add(site.authType);
  }
  return [...authTypes].sort().map((authType) => ({ label: authType, value: authType }));
}

export const WebAutomationSitesListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);

  /** Bumped after a delete to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<WebAutomationSiteDto>();

  const tenantId = currentTenant?.id;
  const projectId = currentProject?.id;
  const hasContext = Boolean(tenantId && projectId);

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<WebAutomationSiteDto>(async (query) => {
        // Unreachable while the guard below renders instead of the grid; throwing rather than
        // silently resolving empty keeps a future regression visible.
        if (!hasContext) throw new Error('No workspace and project selected');

        const sites = await webAutomationService.getSites(toIsActiveFilter(query));
        const searchTerm = typeof query.searchTerm === 'string' ? query.searchTerm : '';
        const authType = typeof query.authType === 'string' ? query.authType : '';

        const filtered = sites.filter(
          (site) =>
            matchesSearch(site, searchTerm) && (authType === '' || site.authType === authType),
        );

        return toClientPage(filtered, query.pageNumber, query.pageSize);
      }),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload. The tenant / project ids are here because the sites
    // endpoint is scoped by the active context, so a switch has to refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasContext, tenantId, projectId, listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<WebAutomationSiteDto>>>(
    () => [
      { field: 'name', header: 'Name', type: 'text', width: '24%', clickable: true },
      { field: 'url', header: 'URL', type: 'text', width: '26%', maxLength: 60, showReadMore: true },
      {
        field: 'authType',
        header: 'Auth',
        type: 'custom',
        width: '16%',
        // The lock is the point of this cell as much as the scheme is: a site whose credentials are
        // stored can be signed into unattended, and one whose credentials are not cannot.
        customCell: (row) => (
          <span className="web-automation-sites__auth">
            <Chip label={row.authType} variant="outlined" />
            {row.hasCredentials && (
              <Lock
                className="web-automation-sites__credential-icon"
                aria-label="Credentials stored (encrypted)"
                role="img"
              />
            )}
          </span>
        ),
      },
      { field: 'attachedTaskCount', header: 'Agents', type: 'number', width: '12%' },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '10%',
        // A Chip rather than the built-in boolean cell: "Active/Inactive" is the vocabulary the
        // rest of the product uses, and it matches the status tabs above the grid.
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
      { field: 'createdAt', header: 'Created', type: 'date', width: '12%', dateFormat: 'locale' },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search sites by name or URL' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Site status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [{ key: 'authType', label: 'Auth', type: 'select', loadOptions: loadAuthTypeOptions }],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<WebAutomationSiteDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View details and attach agents',
        onClick: (row) => navigate(`${ROUTES.WEB_AUTOMATION_SITES}/${row.id}`),
      },
      {
        icon: 'square-pen',
        tooltip: 'Edit site',
        onClick: (row) => navigate(ROUTES.WEB_AUTOMATION_SITE_EDIT.replace(':id', row.id)),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete site',
        className: 'web-automation-sites__action--destructive',
        onClick: (row) => openDelete(row),
      },
    ],
    [navigate, openDelete],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingItem) return;

    await webAutomationService.deleteSite(pendingItem.id);
    addNotification('Site deleted successfully', 'success');
    setListVersion((v) => v + 1);
  }, [pendingItem, addNotification]);

  if (!hasContext) {
    return (
      <div className="web-automation-sites" data-testid="web-automation-site-list-page">
        <ShellHeader
          title="Web Automation Sites"
          description="Web applications the desktop runner can drive, and the agents attached to each."
          data-testid="web-automation-site-list-header"
        />
        <EmptyState
          title="No workspace and project selected"
          description="Sites are project-scoped. Choose a workspace and a project from the switchers above to manage them."
          data-testid="web-automation-site-empty-state"
        />
      </div>
    );
  }

  return (
    <div className="web-automation-sites" data-testid="web-automation-site-list-page">
      <ShellHeader
        title="Web Automation Sites"
        description="Web applications the desktop runner can drive. Attach an agent to a site to make it executable."
        data-testid="web-automation-site-list-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            data-testid="add-web-automation-site-btn"
            onClick={() => navigate(ROUTES.WEB_AUTOMATION_SITE_NEW)}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Create Site
          </Button>
        }
      />

      <div data-testid="web-automation-site-list-grid">
        <CordioAbpPagedDataTable<WebAutomationSiteDto>
          className="web-automation-sites__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(`${ROUTES.WEB_AUTOMATION_SITES}/${row.id}`)}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title="Delete site?"
        entityLabel={pendingItem?.name}
        dialogIcon={<Trash2 className="delete-dialog__icon" />}
        description="Every agent attached to this site is detached and its scripts and run history go with it, so any schedule that drives this site stops working. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        busyLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onCancel={closeDelete}
      />
    </div>
  );
};
